import React from "react";
import { useTranslation } from "react-i18next";
import { renderWithMobileBreaks, renderWithAllBreaks } from "../utils/mobileBreaks";

const DECOR_POLAROIDS = [
  {
    url: "https://images.unsplash.com/photo-1612863217797-5f62b68fba65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwcG9sYXJvaWQlMjBwaG90byUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODE0MTk0NDN8MA&ixlib=rb-4.1.0&q=85",
    rotate: "-rotate-6",
    rotateMobile: "-rotate-3",
    pos: "left-[6%] top-[12%]",
    caption: "08.21 — the window",
  },
  {
    url: "https://images.unsplash.com/photo-1623082185808-579b6093d0dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwcG9sYXJvaWQlMjBwaG90byUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODE0MTk0NDN8MA&ixlib=rb-4.1.0&q=85",
    rotate: "rotate-4",
    rotateMobile: "rotate-2",
    pos: "right-[6%] top-[20%]",
    caption: "midnight — i'm fine",
  },
];

export const Hero = ({ onCta }) => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");
  return (
    <section id="top" className="relative overflow-hidden pt-16 md:pt-24 pb-16 md:pb-32" data-testid="hero-section">
      <div className="lamp" style={{ left: "-12%", top: "8%" }} />
      <div className="lamp" style={{ right: "-14%", bottom: "-10%" }} />

      {/* Desktop-only absolute polaroids (xl+ : ≥1280px) — keeps the romantic
          layered overlay on real desktops but never collides with mobile text.
          Wrapped in an outer div so Tailwind's `hidden` actually wins over the
          custom `.polaroid { display: inline-block }` rule. */}
      {DECOR_POLAROIDS.map((p, i) => (
        <div
          key={`abs-${i}`}
          className={`hidden xl:block absolute ${p.pos} fade-up delay-${i + 2}`}
          aria-hidden="true"
        >
          <div className={`polaroid ${p.rotate}`} style={{ width: 200 }}>
            <img src={p.url} alt="" />
            <div className="mt-3 text-center font-en italic text-[13px] text-[var(--ink-soft)]">
              {p.caption}
            </div>
          </div>
        </div>
      ))}

      {/* Main column — clean vertical flow on mobile, centered editorial on desktop */}
      <div className="max-w-3xl mx-auto px-6 text-center relative flex flex-col items-center space-y-8 md:space-y-10">
        <p className="fade-up text-[11px] uppercase tracking-[0.42em] text-[var(--wax)]" data-testid="hero-kicker">
          {t("hero.kicker")}
        </p>

        <h1
          className={`fade-up delay-1 font-display ${isZh ? "text-[34px] sm:text-5xl lg:text-[56px]" : "text-4xl sm:text-5xl lg:text-6xl"} leading-[1.15] tracking-tight text-[var(--ink)]`}
          data-testid="hero-headline"
        >
          <span className="block">{t("hero.headline_a")}</span>
          {isZh ? (
            <span className="block italic text-[var(--wax)] mt-2 md:whitespace-nowrap">
              {t("hero.headline_b1")}
              <br className="md:hidden" />
              {t("hero.headline_b2")}
            </span>
          ) : (
            <>
              <span className="block italic text-[var(--wax)] mt-2">{t("hero.headline_b1")}</span>
              <span className="block italic text-[var(--wax)]">{t("hero.headline_b2")}</span>
            </>
          )}
        </h1>

        <div
          className="fade-up delay-2 text-[17px] md:text-[19px] leading-[1.85] md:leading-[1.9] text-[var(--ink-soft)] max-w-2xl mx-auto space-y-4"
          data-testid="hero-subtitle"
        >
          <p>{renderWithMobileBreaks(t("hero.subtitle_a1"))}</p>
          <p>{renderWithMobileBreaks(t("hero.subtitle_a2"))}</p>
          <p>{isZh ? renderWithMobileBreaks(t("hero.subtitle_b")) : renderWithAllBreaks(t("hero.subtitle_b"))}</p>
        </div>

        {/* Mobile / tablet polaroid row — inline, NEVER absolute, never overlapping.
            Hidden on xl+ where the absolute desktop overlay takes over. */}
        <div
          className="xl:hidden w-full flex flex-row items-start justify-center gap-5 sm:gap-8 pt-2 pb-2 fade-up delay-2"
          data-testid="hero-polaroids-mobile"
          aria-hidden="true"
        >
          {DECOR_POLAROIDS.map((p, i) => (
            <div
              key={`inline-${i}`}
              className={`polaroid ${p.rotateMobile} sm:${p.rotate} flex-1 max-w-[160px] sm:max-w-[200px]`}
            >
              <img src={p.url} alt="" className="block w-full h-auto" />
              <div className="mt-2 text-center font-en italic text-[12px] sm:text-[13px] text-[var(--ink-soft)]">
                {p.caption}
              </div>
            </div>
          ))}
        </div>

        <div className="fade-up delay-3 flex flex-wrap items-center justify-center gap-5 sm:gap-6">
          <button
            onClick={onCta}
            data-testid="hero-cta"
            className="btn-wax px-8 sm:px-9 py-4 text-[13px] uppercase tracking-[0.22em] min-h-[48px]"
          >
            {t("hero.cta")}
          </button>
          <img
            src="/press-kit/badge-365d-192.png"
            alt="365 days sealed"
            data-testid="hero-365d-badge"
            width="88"
            height="88"
            style={{
              width: 88,
              height: 88,
              objectFit: "contain",
              imageRendering: "-webkit-optimize-contrast",
              filter: "drop-shadow(0 6px 14px rgba(110,45,40,0.32))",
            }}
          />
        </div>
      </div>
    </section>
  );
};
